import { useMaintenanceBanner } from "../../hooks/useMaintenanceBanner";

/**
 * MaintenanceBanner Component
 * 
 * Displays maintenance notifications from the T1 gateway.
 * Shows different styles for regular maintenance vs outage events.
 * 
 * Follows servicing-login pattern:
 * - Shows banner 2 hours before maintenance starts (warning period)
 * - Shows outage banner during actual maintenance window
 * - Uses server time to avoid client clock skew
 * 
 * API Response Structure:
 * - showBanner: boolean - Whether banner is enabled (from API)
 * - outage: boolean - Whether this is an outage (vs scheduled maintenance)
 * - bannerMessage: string - The message to display
 * - startDateTime: string - Maintenance start time
 * - endDateTime: string - Maintenance end time
 * 
 * Computed Fields (based on server time):
 * - shouldShowBanner: boolean - Time-based check if banner should be shown
 * - isInMaintenanceWindow: boolean - 2 hours before maintenance
 * - isInOutageWindow: boolean - During actual maintenance
 * 
 * Layout: Positioned just below header, full width from left to right edge
 */
export function MaintenanceBanner() {
  const { 
    shouldShowBanner, 
    message, 
    isLoading, 
    error,
  } = useMaintenanceBanner();

  // Don't show anything while loading or if there's an error
  if (isLoading) {
    return null;
  }

  // Log errors but fail gracefully
  if (error) {
    console.warn('[MaintenanceBanner] Failed to load maintenance banner data:', error);
    return null;
  }

  // Use shouldShowBanner which combines API flag + time-based check
  if (!shouldShowBanner || !message) {
    return null;
  }

  return (
    <div 
      style={{ 
        width: '100%',
        margin: 0,
        padding: 0,
      }}
    >
      <div
        role="alert"
        aria-live="polite"
        style={{
          backgroundColor: '#fdf5ea',
          padding: '12px 16px',
          margin: 0,
          fontFamily: 'Poppins, sans-serif',
          fontWeight: 500,
          fontSize: '15px',
          lineHeight: '22.5px',
          color: '#825417',
          width: '100%',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          {/* Warning triangle SVG icon - matches servicing-login design */}
          <div style={{ flexShrink: 0 }}>
            <svg
              width="22"
              height="19"
              viewBox="0 0 22 19"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M0.916626 18.2534H21.0833L11 0.836767L0.916626 18.2534ZM11.9166 15.5034H10.0833V13.6701H11.9166V15.5034ZM11.9166 11.8368H10.0833V8.1701H11.9166V11.8368Z"
                fill="#825417"
              />
            </svg>
          </div>
          
          {/* Message text */}
          <div style={{ flex: 1 }}>
            <p style={{ padding: 0, margin: 0, textAlign: 'left' }}>
              {message}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
